// Safely check if running on native platform
// This file is designed to work on both web and native platforms

let Capacitor = null;
let PushNotifications = null;
let StatusBar = null;
let Style = null;
let SplashScreen = null;

// Try to load Capacitor - it may not be available on web
try {
  Capacitor = require('@capacitor/core').Capacitor;
} catch (e) {
  console.log('Capacitor core not available');
}

// Check if running on native platform
export const isNativePlatform = () => {
  try {
    return Capacitor && Capacitor.isNativePlatform();
  } catch (e) {
    return false;
  }
};

// Lazy load native plugins only when needed
const loadNativePlugins = async () => {
  if (!isNativePlatform()) return false;
  
  try {
    if (!PushNotifications) {
      const pushModule = await import('@capacitor/push-notifications');
      PushNotifications = pushModule.PushNotifications;
    }
    if (!StatusBar) {
      const statusModule = await import('@capacitor/status-bar');
      StatusBar = statusModule.StatusBar;
      Style = statusModule.Style;
    }
    if (!SplashScreen) {
      const splashModule = await import('@capacitor/splash-screen');
      SplashScreen = splashModule.SplashScreen;
    }
    return true;
  } catch (e) {
    console.error('Error loading native plugins:', e);
    return false;
  }
};

// Initialize native features
export const initializeNativeFeatures = async () => {
  if (!isNativePlatform()) {
    console.log('Not running on native platform, skipping native initialization');
    return;
  }

  const loaded = await loadNativePlugins();
  if (!loaded) return;

  try {
    // Configure status bar
    if (StatusBar && Style) {
      await StatusBar.setBackgroundColor({ color: '#121212' });
      await StatusBar.setStyle({ style: Style.Dark });
      console.log('Status bar configured');
    }
  } catch (error) {
    console.error('Error configuring status bar:', error);
  }

  try {
    // Hide splash screen after app is ready
    if (SplashScreen) {
      await SplashScreen.hide();
      console.log('Splash screen hidden');
    }
  } catch (error) {
    console.error('Error hiding splash screen:', error);
  }
};

// Push Notifications
export const initializePushNotifications = async () => {
  if (!isNativePlatform()) {
    console.log('Push notifications only available on native platform');
    return null;
  }

  const loaded = await loadNativePlugins();
  if (!loaded || !PushNotifications) return false;

  try {
    // Request permission
    const permStatus = await PushNotifications.requestPermissions();
    
    if (permStatus.receive === 'granted') {
      // Register with Apple / Google to receive push via APNS/FCM
      await PushNotifications.register();
      console.log('Push notifications registered');
      return true;
    } else {
      console.log('Push notification permission denied');
      return false;
    }
  } catch (error) {
    console.error('Error initializing push notifications:', error);
    return false;
  }
};

// Add push notification listeners
export const addPushNotificationListeners = async (callbacks = {}) => {
  if (!isNativePlatform()) {
    return () => {}; // Return empty cleanup function
  }

  const loaded = await loadNativePlugins();
  if (!loaded || !PushNotifications) {
    return () => {};
  }

  const { onRegistration, onRegistrationError, onPushReceived, onPushActionPerformed } = callbacks;

  // On successful registration
  const registrationListener = await PushNotifications.addListener('registration', (token) => {
    console.log('Push registration success, token: ' + token.value);
    if (onRegistration) onRegistration(token.value);
  });

  // On registration error
  const errorListener = await PushNotifications.addListener('registrationError', (error) => {
    console.error('Push registration error:', error);
    if (onRegistrationError) onRegistrationError(error);
  });

  // On push notification received (app in foreground)
  const receivedListener = await PushNotifications.addListener('pushNotificationReceived', (notification) => {
    console.log('Push notification received:', notification);
    if (onPushReceived) onPushReceived(notification);
  });

  // On push notification action performed (user tapped notification)
  const actionListener = await PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
    console.log('Push notification action performed:', action);
    if (onPushActionPerformed) onPushActionPerformed(action);
  });

  // Return cleanup function
  return async () => {
    await registrationListener.remove();
    await errorListener.remove();
    await receivedListener.remove();
    await actionListener.remove();
  };
};

// Get device token for push notifications
export const getPushToken = async () => {
  if (!isNativePlatform()) {
    return null;
  }

  const loaded = await loadNativePlugins();
  if (!loaded || !PushNotifications) return null;

  return new Promise((resolve) => {
    const listener = PushNotifications.addListener('registration', (token) => {
      listener.remove();
      resolve(token.value);
    });

    PushNotifications.register().catch(() => {
      listener.remove();
      resolve(null);
    });
  });
};

export default {
  isNativePlatform,
  initializeNativeFeatures,
  initializePushNotifications,
  addPushNotificationListeners,
  getPushToken
};
